import { DollarOutlined, EditOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Button, Modal, Table, Tooltip } from 'antd';
import TimeLogsRulesModal from 'components/TimeLogs/TimeLogsRulesModal';
import Title from 'antd/es/typography/Title';
import { timeActivitiesApi } from 'Api/timeActivities';
import { timeLogsAssignmentApi } from 'Api/timeLogsAssignment';
import Buttons from 'components/Global/Buttons';
import { DateDisplay } from 'components/Global/DateDisplay';
import InputTimeLogsHoursField from 'components/InputTimeLogsHoursField';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
	capitalizeFirstLetter,
	formatNumber,
	formatToDateOnly,
	minutesToTimeString,
	timeStringToMinutes,
	toastText,
} from 'utils/utils';
import styles from './index.module.scss';

const { Column } = Table;

type Props = {
	tableData: any;
	totalRecords: number;
	currentPage: number;
	pageSize: number;
	tableChangeHandler: (pagination: any, filter: any, sorter: any) => void;
	isLoading: boolean;
	dateRange: [string, string];
	staffCode: any;
	fetchEmployeesTimeActivities: any;
	payrollViewEnabled: boolean;
};

const TimeActivitiesTable = (props: Props) => {
	const {
		tableData,
		totalRecords,
		currentPage,
		pageSize,
		tableChangeHandler,
		isLoading,
		dateRange,
		fetchEmployeesTimeActivities,
		staffCode,
		payrollViewEnabled,
	} = props;

	const { data } = useSelector((state: any) => state?.userProfile);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [isTimeLogsRulesModalOpen, setIsTimeLogsRulesModalOpen] =
		useState(false);
	const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
	const [employeeDayWiseHours, setEmployeeDayWiseHours] = useState<any>([]);
	const [isTimeLogsLoading, setIsTimeLogsLoading] = useState(false);
	const [isTimeLogsSaving, setIsTimeLogsSaving] = useState(false);

	const { data: profileData } = useSelector(
		(state: any) => state?.userProfile
	);

	const handleEmployeeClick = async (employee: any) => {
		setSelectedEmployee(employee);
		setIsModalVisible(true);

		const employeeId =
			employee.id ||
			employee.employeeId ||
			employee.userId ||
			employee.empId;

		if (!employeeId) {
			setIsTimeLogsLoading(false);
			return;
		}

		// Fetch employee day-wise logs
		await fetchEmployeeDayWiseLogs(employeeId, 1, 50, 'date', 'asc');
	};

	const myButtons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'secondary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			disabled: isTimeLogsSaving,
			isSubmit: false,
			onclick: () => {
				handleModalClose();
			},
		},
		{
			text: 'Save',
			isLoading: isTimeLogsSaving,
			className: 'primary-button',
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
			isSubmit: true,
			disabled: isTimeLogsSaving,
			onclick: () => {
				handleSubmit();
			},
		},
	];

	const handleSubmit = async () => {
		try {
			setIsTimeLogsSaving(true);

			const employeeHoursData = employeeDayWiseHours
				.filter((item: any) => !item.isTotal && item.shiftId !== null)
				.map((item: any) => {
					const regularMinutes = timeStringToMinutes(
						item.regularHours || '00:00'
					);
					const overtimeMinutes = timeStringToMinutes(
						item.overtimeHours || '00:00'
					);

					const attendanceStatus =
						regularMinutes === 0 && overtimeMinutes === 0
							? 'ABSENT'
							: 'PRESENT';

					return {
						employeeId: item.employeeId,
						regularHours: regularMinutes,
						overtimeHours: overtimeMinutes,
						date: item.date,
						shiftId: item.shiftId,
						timeShiftHours: timeStringToMinutes(
							item.timeShiftHours || '00:00'
						),
						attendanceStatus: attendanceStatus,
					};
				});
			const submitData = {
				employeeId: selectedEmployee.employeeId,
				startDate: formatToDateOnly(dayjs(dateRange[0])),
				endDate: formatToDateOnly(dayjs(dateRange[1])),
				employeeHours: employeeHoursData,
			};
			const response =
				await timeLogsAssignmentApi.createBulkTimeLogsForEmployee(
					submitData
				);

			if (response?.data) {
				toastText('Time logs submitted successfully', 'success');
			}
			handleModalClose();
			fetchEmployeesTimeActivities();
		} catch (error: any) {
			const message =
				error?.response?.data?.message || 'Failed to submit time logs';
			toastText(message, 'error');
		} finally {
			setIsTimeLogsSaving(false);
		}
	};

	const fetchEmployeeDayWiseLogs = async (
		employeeId: string,
		page: number = 1,
		pageSize: number = 10,
		sortBy: string = 'date',
		sortOrder: string = 'asc'
	) => {
		if (!employeeId) {
			return;
		}

		setIsTimeLogsLoading(true);
		try {
			const query = {
				employeeId: employeeId,
				startDate: dayjs(dateRange[0]).format('YYYY-MM-DD'),
				endDate: dayjs(dateRange[1]).format('YYYY-MM-DD'),
				page: page,
				pageSize: pageSize,
				sortBy: sortBy,
				sortOrder: sortOrder,
			};

			// Call your API endpoint
			const response =
				await timeActivitiesApi.getEmployeeTimeActivitiesDayWise(query);

			if (response.data && response.data.data) {
				setSelectedEmployee((prev: any) => ({
					...prev,
					isPayrollRun: response.data.data.employee.isPayrollRun,
					payrollStatus: response.data.data.employee.payrollStatus,
				}));
				const logs = response.data.data.dayWiseLogs.map(
					(log: any, index: number) => ({
						...log,
						key: log.id || `${employeeId}-${index}`,
						Date: log.isTotal
							? 'Total'
							: `${log.date} (${log.day})`,
						employeeId: employeeId,
					})
				);

				setEmployeeDayWiseHours(logs);
			} else {
				setEmployeeDayWiseHours([]);
			}
		} catch (error: any) {
			setEmployeeDayWiseHours([]);
		} finally {
			setIsTimeLogsLoading(false);
		}
	};

	// Calculate totals from employee data
	const calculateTotals = (employees: any[]) => {
		const totals = employees.reduce(
			(acc, emp) => {
				if (!emp.isTotal) {
					acc.totalRegularHours += timeStringToMinutes(
						emp.regularHours || '00:00'
					);
					acc.totalOvertimeHours += timeStringToMinutes(
						emp.overtimeHours || '00:00'
					);
					acc.totalTimeShiftHours += timeStringToMinutes(
						emp.timeShiftHours || '00:00'
					);
					acc.totalTotalHours += timeStringToMinutes(
						emp.totalHours || '00:00'
					);
				}
				return acc;
			},
			{
				totalRegularHours: 0,
				totalOvertimeHours: 0,
				totalTimeShiftHours: 0,
				totalTotalHours: 0,
			}
		);

		return {
			key: 'totals',
			Date: 'Total',
			day: '',
			regularHours: minutesToTimeString(totals.totalRegularHours),
			overtimeHours: minutesToTimeString(totals.totalOvertimeHours),
			timeShiftHours: minutesToTimeString(totals.totalTimeShiftHours),
			totalHours: minutesToTimeString(totals.totalTotalHours),
			attendanceStatus: '',
			shiftType: null,
			shiftName: null,
			shiftId: null,
			isHoliday: false,
			holidayName: null,
			isSunday: false,
			leaveDetails: {
				isPaidLeave: false,
				isLwp: false,
			},
			submittedBy: '',
			submittedOn: null,
			isUpdatedEntry: false,
			isTotal: true,
		};
	};

	const handleLogChange = (id: string, name: string, value: any) => {
		const updatedData = employeeDayWiseHours.map((item: any) => {
			let isForeman = item.isForeman;
			const REGULAR_HOURS = isForeman ? 720 : 480;
			if (item.key === id && !item.isTotal) {
				if (name === 'totalHours') {
					const totalMinutes = timeStringToMinutes(value);

					// Check if it's a holiday or Sunday for overtime logic
					const shouldTreatAsOvertime = item.isSunday;

					let calculatedRegular = '00:00';
					let calculatedOvertime = '00:00';

					if (shouldTreatAsOvertime) {
						calculatedOvertime = value;
					} else {
						const regularMinutes = Math.min(
							totalMinutes,
							REGULAR_HOURS
						); // 8 hours = 480 minutes
						const overtimeMinutes = Math.max(
							0,
							totalMinutes - REGULAR_HOURS
						);
						calculatedRegular = minutesToTimeString(regularMinutes);
						calculatedOvertime =
							minutesToTimeString(overtimeMinutes);
					}

					return {
						...item,
						totalHours: value,
						regularHours: calculatedRegular,
						overtimeHours: calculatedOvertime,
						timeShiftHours:
							item.shiftType === 'NIGHT' ? value : '00:00',
					};
				} else {
					return {
						...item,
						[name]: value,
					};
				}
			}
			return item;
		});

		// Recalculate totals
		const employees = updatedData.filter((item: any) => !item.isTotal);
		const totalsRow = calculateTotals(employees);
		const finalData = [...employees, totalsRow];

		setEmployeeDayWiseHours(finalData);
	};

	const handleInputChange = (
		employeeId: string,
		field: string,
		value: any,
		date: any
	) => {
		// Find the record by employeeId and date combination
		const recordKey = employeeDayWiseHours.find(
			(item: any) =>
				item.employeeId === employeeId &&
				!item.isTotal &&
				item.date === date
		)?.id;

		if (recordKey) {
			handleLogChange(recordKey, field, value);
		}
	};

	// Handle modal table changes (pagination and sorting)
	const handleModalTableChange = (
		pagination: any,
		filters: any,
		sorter: any
	) => {
		const newPage = pagination.current || 1;
		const newPageSize = pagination.pageSize || 10;
		const newSortBy = sorter.field || 'date';
		const newSortOrder =
			sorter.order === 'ascend'
				? 'asc'
				: sorter.order === 'descend'
				? 'desc'
				: 'asc';

		if (selectedEmployee) {
			const employeeId =
				selectedEmployee.id ||
				selectedEmployee.employeeId ||
				selectedEmployee.userId ||
				selectedEmployee.empId;
			if (employeeId) {
				fetchEmployeeDayWiseLogs(
					employeeId,
					newPage,
					newPageSize,
					newSortBy,
					newSortOrder
				);
			}
		}
	};

	// Handle modal close
	const handleModalClose = () => {
		setIsModalVisible(false);
		setSelectedEmployee(null);
		setEmployeeDayWiseHours([]);
	};

	// Format salary rate
	const formatSalaryRate = (employee: any) => {
		if (!employee) return '';

		if (employee.isUSD) {
			return formatNumber(
				Number(employee.monthlySalary || 0),
				data?.CompanyCurrencies,
				true
			);
		} else {
			return formatNumber(
				Number(employee.monthlySalary || 0),
				data?.CompanyCurrencies
			);
		}
	};

	// Get pay period display
	const getPayPeriodDisplay = () => {
		if (!dateRange || !dateRange[0] || !dateRange[1]) return '';

		const startMonth = dayjs(dateRange[0]).format('MMMM YYYY');
		const endMonth = dayjs(dateRange[1]).format('MMMM YYYY');

		return startMonth === endMonth
			? startMonth
			: `${dayjs(dateRange[0]).format('MMM')} - ${endMonth}`;
	};

	return (
		<>
			<div>
				<Table
					dataSource={tableData}
					scroll={{
						y: 'calc(100vh - 360px)',
						x: payrollViewEnabled ? 1200 : 'auto',
					}}
					pagination={{
						total: totalRecords,
						current: currentPage,
						pageSize: pageSize,
						showSizeChanger: false,
						pageSizeOptions: [10, 20, 50, 100, 200],
						showTotal: (total) => (
							<span
								style={{
									marginLeft: '15px',
									fontSize: '15px',
								}}
							>
								Total No. of Employees : {total}
							</span>
						),
					}}
					onChange={tableChangeHandler}
					rowKey={(record) => record.id}
					loading={isLoading}
				>
					<Column
						title="Employee Name | Code"
						dataIndex="optionalEmployeeId"
						key="optionalEmployeeId"
						className="bg-white"
						width={300}
						fixed="left"
						sorter={true}
						render={(text, record: any) => (
							<>
								<span
									onClick={() => {
										// Only allow click for hourly employees
										if (staffCode === 'HOURLY') {
											handleEmployeeClick(record);
										}
									}}
									style={{
										cursor:
											staffCode === 'HOURLY'
												? 'pointer'
												: 'default',
										textDecoration:
											staffCode === 'HOURLY'
												? 'underline'
												: 'none',
									}}
									title={
										staffCode === 'HOURLY'
											? 'Click to view day-wise logs'
											: ''
									}
								>
									{record.firstName} {record.midName}{' '}
									{record.lastName}
								</span>{' '}
								|{' '}
								<span className="color-purple">
									{record?.optionalEmployeeId}
								</span>
								{record.hasAdjustedLogs && (
									<Tooltip title="Time logs for this employee have been Adjusted for the current month.">
										<EditOutlined
											style={{
												marginLeft: '8px',
												color: '#584495',
											}}
										/>
									</Tooltip>
								)}
							</>
						)}
					/>
					<Column
						title={
							<>
								<div>Salary Rate</div>
							</>
						}
						dataIndex="monthlySalary"
						className="bg-white"
						key="monthlySalary"
						width={150}
						render={(value: string, record: any) => {
							if (record.isUSD) {
								return formatNumber(
									Number(value),
									data?.CompanyCurrencies,
									true
								);
							} else {
								return formatNumber(
									Number(value),
									data?.CompanyCurrencies
								);
							}
						}}
					/>
					<Column
						title="Regular Hours"
						dataIndex="totalRegularHours"
						key="totalRegularHours"
						className="bg-white"
						width={150}
					/>
					<Column
						title="Weekday OT"
						dataIndex="weekdaysOT"
						className="bg-white"
						key="weekdaysOT"
						width={150}
					/>
					<Column
						title="Double OT"
						dataIndex="doubleOT"
						className="bg-white"
						key="doubleOT"
						width={150}
					/>
					<Column
						title="Shift Difference"
						dataIndex="totalTimeShiftHours"
						className="bg-white"
						width={150}
						key="totalTimeShiftHours"
					/>
					<Column
						title="Log Source"
						dataIndex="source"
						className="bg-white"
						width={150}
						key="source"
					/>
					<Column
						title="Total Hours"
						dataIndex="totalHours"
						key="totalHours"
						className="bg-white"
						width={150}
						render={(value: string) => {
							return (
								<span className="color-purple"> {value}</span>
							);
						}}
					/>
					{payrollViewEnabled && (
						<Column
							title="Payroll Hours"
							key="payrollHours"
							className="bg-white"
							width={180}
							fixed="right"
							render={(text, record: any) => {
								const regularHours =
									record.afterBalanceHours
										.totalRegularHours || '00:00';
								const weekdayOT =
									record.afterBalanceHours.weekdaysOT ||
									'00:00';
								const doubleOT =
									record.afterBalanceHours.doubleOT ||
									'00:00';
								const shiftDiff =
									record.afterBalanceHours
										.totalTimeShiftHours || '00:00';

								return (
									<div
										style={{
											fontSize: '11px',
											lineHeight: '1.2',
										}}
									>
										{/* Regular Hours - Green */}
										<span
											className={styles['regular-color']}
										>
											R : {regularHours}
										</span>
										<span
											style={{
												color: '#999',
												margin: '0 2px',
											}}
										>
											|
										</span>
										<span className={styles['ot-color']}>
											OT : {weekdayOT}
										</span>

										{/* Double OT - Red */}
										<div
											style={{
												color: '#ff4d4f',
												marginBottom: '1px',
											}}
										>
											<span
												className={styles['ot-color']}
											>
												D-OT : {doubleOT}
											</span>
											<span
												style={{
													color: '#999',
													margin: '0 2px',
												}}
											>
												|
											</span>
											<span
												className={
													styles['shift-color']
												}
											>
												SDH : {shiftDiff}
											</span>
										</div>
									</div>
								);
							}}
						/>
					)}
				</Table>
			</div>

			<Modal
				open={isModalVisible}
				onCancel={handleModalClose}
				width={1200}
				centered
				closable={false}
				footer={[
					<div
						key={'wrapper'}
						className={styles['confirm-modal__button']}
					>
						<Buttons buttons={myButtons}></Buttons>
					</div>,
				]}
				title={
					<div className={`${styles['modal']} modal`}>
						<div
							className={styles['modal-header']}
							style={{
								display: 'flex',
								alignItems: 'center',
								flexWrap: 'wrap',
								gap: '4px 12px',
								justifyContent: 'flex-start',
							}}
						>
							<Title level={4} style={{ margin: 0 }}>
								<span>
									📋 Day-wise Entry for{' '}
									<span style={{ color: '#584495' }}>
										{selectedEmployee?.firstName}{' '}
										{selectedEmployee?.midName}{' '}
										{selectedEmployee?.lastName} (
										{selectedEmployee?.optionalEmployeeId})
									</span>
								</span>
							</Title>
							<Tooltip title="How hours are calculated">
								<Button
									type="link"
									size="small"
									style={{
										padding: 0,
										height: 'auto',
										fontSize: '14px',
										color: '#584495',
									}}
									icon={<QuestionCircleOutlined />}
									onClick={() =>
										setIsTimeLogsRulesModalOpen(true)
									}
								>
									How hours work
								</Button>
							</Tooltip>
						</div>

						{/* Pay Period and Salary Rate Info Row */}
						<div className={styles['info-row']}>
							<div className={styles['info-items']}>
								<div className={styles['info-item']}>
									<span className={styles['info-label']}>
										Code :
									</span>
									<span className={styles['info-value']}>
										{selectedEmployee?.optionalEmployeeId ||
											'-'}
										{selectedEmployee?.isPayrollRun &&
											selectedEmployee?.payrollStatus && (
												<Tooltip
													title={
														selectedEmployee.payrollStatus ===
															'APPROVED' ||
														selectedEmployee.payrollStatus ===
															'FINALIZE'
															? 'Payroll is Approved/Finalized'
															: selectedEmployee.payrollStatus ===
															  'DRAFT'
															? 'Payroll is in Draft'
															: 'Payroll is Pending'
													}
												>
													<DollarOutlined
														style={{
															marginLeft: '8px',
															color:
																selectedEmployee.payrollStatus ===
																	'APPROVED' ||
																selectedEmployee.payrollStatus ===
																	'FINALIZE'
																	? 'green'
																	: selectedEmployee.payrollStatus ===
																	  'DRAFT'
																	? 'orange'
																	: 'grey',
														}}
													/>
												</Tooltip>
											)}
									</span>
								</div>
								<div className={styles['info-item']}>
									<span className={styles['info-label']}>
										Salary Rate :
									</span>
									<span className={styles['info-value']}>
										{formatSalaryRate(selectedEmployee)}
									</span>
								</div>
								<div className={styles['info-item']}>
									<span className={styles['info-label']}>
										Pay Period :
									</span>
									<span className={styles['info-value']}>
										{getPayPeriodDisplay()}
									</span>
								</div>
							</div>
						</div>
					</div>
				}
			>
				<div className={styles['modal-body']}>
					<Table
						dataSource={employeeDayWiseHours}
						scroll={{ y: 'calc(100vh - 300px)' }}
						pagination={false}
						onChange={handleModalTableChange}
						rowKey={(record) => record.id}
						loading={isTimeLogsLoading}
						rowClassName={(record: any) => {
							return record.isTotal ? 'sticky-row' : '';
						}}
						size="small"
					>
						<Column
							title="Date (Day)"
							dataIndex="date"
							key="date"
							width="15%"
							className="bg-white"
							sorter={true}
							render={(value: string, record: any) => {
								if (record.isTotal)
									return (
										<span className="color-purple font-bold">
											Total
										</span>
									);

								const isAbsent =
									record.attendanceStatus === 'ABSENT';
								const isHoliday = record.isHoliday || false;
								const holidayName = record.holidayName || '';
								const isSunday = record.isSunday || false;

								// Tooltip text for attendance
								const attendanceTooltip = record.leaveDetails
									?.isPaidLeave
									? 'On Full Day Paid leave'
									: record.leaveDetails?.isLwp
									? 'On Full Day LWP Leave'
									: isHoliday
									? holidayName
									: isAbsent
									? 'Marked as Absent'
									: '';

								// Payroll status icon
								const payrollIcon = record.isPayrollRun &&
									record?.payrollStatus && (
										<Tooltip
											title={
												record.payrollStatus ===
													'APPROVED' ||
												record.payrollStatus ===
													'FINALIZE'
													? 'Payroll is Approved/Finalized'
													: record.payrollStatus ===
													  'DRAFT'
													? 'Payroll is in Draft'
													: 'Payroll is Pending'
											}
										>
											<DollarOutlined
												style={{
													color:
														record.payrollStatus ===
															'APPROVED' ||
														record.payrollStatus ===
															'FINALIZE'
															? 'green'
															: record.payrollStatus ===
															  'DRAFT'
															? 'orange'
															: 'grey',
												}}
											/>
										</Tooltip>
									);

								let dateClass;
								if (isHoliday) {
									dateClass = styles['date-holiday'];
								} else if (isSunday) {
									dateClass = styles['date-sunday'];
								} else if (isAbsent) {
									dateClass = styles['date-absent'];
								}
								return (
									<Tooltip title={attendanceTooltip}>
										<span
											style={{
												color:
													record.leaveDetails
														?.isLwp ||
													record.leaveDetails
														?.isPaidLeave ||
													isAbsent
														? 'red'
														: 'inherit',
												display: 'flex',
												alignItems: 'center',
												gap: '0.5rem',
											}}
										>
											{payrollIcon}
											<div className={dateClass}>
												<DateDisplay
													date={value}
													dateFormat={
														profileData?.companyDateFormat ??
														'DD/MM/YYYY'
													}
													compact={true}
													isHoliday={isHoliday}
													isAbsent={isAbsent}
													highlightSpecialDays={true}
												/>
											</div>
										</span>
									</Tooltip>
								);
							}}
						/>

						<Column
							title="Shift Type"
							dataIndex="shiftType"
							key="shiftType"
							className="bg-white"
							width="12%"
							render={(text, record: any) => (
								<span
									style={{
										fontWeight: record.isTotal
											? 'bold'
											: 'normal',
									}}
								>
									{record.isTotal
										? ''
										: capitalizeFirstLetter(text) || '-'}
								</span>
							)}
						/>

						<Column
							title="Total Worked Hours"
							dataIndex="totalHours"
							key="totalHours"
							width="20%"
							className="bg-white"
							render={(value: any, record: any) => {
								if (record.isTotal) {
									return (
										<p
											className="color-purple font-bold"
											style={{
												textAlign: 'right',
												width: '60%',
											}}
										>
											{record.totalHours}
										</p>
									);
								}
								if (!record.shiftId) {
									return (
										<p
											style={{
												textAlign: 'right',
												width: '55%',
											}}
										>
											-
										</p>
									);
								}
								return (
									<InputTimeLogsHoursField
										value={record.totalHours}
										onChange={(newValue) =>
											handleInputChange(
												record.employeeId,
												'totalHours',
												newValue,
												record.date
											)
										}
										label=""
										required={false}
										name="totalHours"
										isError={false}
										disabled={
											record.leaveDetails?.isLwp ||
											record.leaveDetails?.isPaidLeave ||
											selectedEmployee?.isPayrollRun
										}
										showLabel={false}
										size="middle"
										style={{
											width: '60%',
											textAlign: 'right',
										}}
										is8hoursRequired={
											record?.isHoliday &&
											!record?.isSunday
										}
										isForeman={record?.isForeman}
									/>
								);
							}}
						/>

						<Column
							title="Time Shift Difference"
							dataIndex="timeShiftHours"
							key="timeShiftHours"
							className="bg-white"
							width="20%"
							render={(text, record: any) => (
								<span
									style={{
										fontWeight: record.isTotal
											? 'bold'
											: 'normal',
										color: record.isTotal
											? '#584495'
											: 'inherit',
									}}
								>
									{text || '-'}
								</span>
							)}
						/>
					</Table>
				</div>
			</Modal>

			<TimeLogsRulesModal
				open={isTimeLogsRulesModalOpen}
				onClose={() => setIsTimeLogsRulesModalOpen(false)}
			/>
		</>
	);
};

export default TimeActivitiesTable;
